import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useWidth } from "hooks/useWidth";
import TextTruncate from "components/TextTruncate";
import Button, { ButtonStyle } from "components/Button";
import Select from "components/Controls/Select";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import SpaceviewerGraph from "./components/SpaceviewerGraph";
import Slider from "./components/Slider";
import ColorMap from "./components/ColorMap";
import ProximityMap from "./components/ProximityMap";
import ConfidenceMap from "./components/ConfidenceMap";
import OptimizationPreviewLegend from "./components/OptimizationPreviewLegend";
import ResultElementLegend from "./components/ResultElementLegend";
import Density from "./components/Density";
import Loader from "components/Loader";
import { changeAxis, ColorMapType, DEFAULT_LABEL, getColorMap, getIndexedParameters, INSIGHT_LABEL, POINT_COLORS, prepare1DSpaceviewerData, prepare2DSpaceviewerData, prepareInitialSlidersValues, prepareSpaceviewerData, SpaceviewerMode, } from "./utils";
import { isNotEmpty } from "utils/validate";
import { compareResultItemIds } from "utils/data";
import { getTotalRuns } from "store/rs-workspace/utils/calculations.utils";
import { calculationSpaceviewerResultsSelector, getActiveResultSetIdSelector, } from "store/rs-workspace/selectors/results.selectors";
import * as styles from "./styles.module.scss";
export var SpaceviewerAppearance;
(function (SpaceviewerAppearance) {
    SpaceviewerAppearance["OptimizationPreview"] = "optimization_preview";
    SpaceviewerAppearance["SimulationResult"] = "simulation_result";
    SpaceviewerAppearance["InsightResult"] = "insight_result";
})(SpaceviewerAppearance || (SpaceviewerAppearance = {}));
const DEFAULT_RANGE = [0, 100];
const Spaceviewer = ({ data, selectedAxisIndexes, changeSelectedAxisIndexes, changeConfidence, changeDensity, error, legendData, appearance, elementId, scrollElement, insightError = false, inProgress = false, recalculateWidth = false, spaceviewerUid, }) => {
    const [slidersInitialValues, setSlidersInitialValues] = useState([]);
    const [slidersValues, setSlidersValues] = useState([]);
    const [selectedPoint, setSelectedPoint] = useState();
    const [optimizedPoint, setOptimizedPoint] = useState();
    const [isInOptimum, setIsInOptimum] = useState(false);
    const [xAxis, setXAxis] = useState(null);
    const [yAxis, setYAxis] = useState(null);
    const [sliders, setSliders] = useState([]);
    const [range, setRange] = useState(DEFAULT_RANGE);
    const [colorMapType, setColorMapType] = useState(ColorMapType.High);
    const [colorPoints, setColorPoints] = useState(POINT_COLORS);
    const { widgetRef, widgetWidth } = useWidth(undefined, recalculateWidth);
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const spaceViewerResults = useSelector(calculationSpaceviewerResultsSelector);
    const sourceResult = useMemo(() => spaceViewerResults
        ?.find((item) => compareResultItemIds(item, { resultSetElementId: elementId, resultSetId }))
        ?.sources.find((item) => item.sourceId === data?.source?.id), [elementId, resultSetId, data?.source?.id, spaceViewerResults]);
    const totalRunsCount = useMemo(() => !sourceResult?.accuracy
        ? 0
        : getTotalRuns({
            ...sourceResult?.accuracy,
            numberOfOptimisationSteps: null,
        }, false), [sourceResult?.accuracy]);
    const isEmpty = useMemo(() => (data?.values?.length ?? 0) === 0, [data?.values?.length]);
    const isResultElement = useMemo(() => appearance === SpaceviewerAppearance.InsightResult ||
        appearance === SpaceviewerAppearance.SimulationResult, [appearance]);
    const mode = useMemo(() => {
        if (data?.parameters?.length === 1) {
            return SpaceviewerMode.D1;
        }
        else if (data?.parameters?.length === 2) {
            return SpaceviewerMode.D2;
        }
        else if (data?.parameters?.length > 2) {
            return SpaceviewerMode.D3;
        }
        return SpaceviewerMode.D1;
    }, [data?.parameters?.length]);
    const paramsForXAxis = useMemo(() => [yAxis ?? {}, ...sliders].map(({ name, index }) => ({
        label: name,
        value: index,
    })), [sliders, yAxis]);
    const paramsForYAxis = useMemo(() => [xAxis ?? {}, ...sliders].map(({ name, index }) => ({
        label: name,
        value: index,
    })), [sliders, xAxis]);
    const points = useMemo(() => {
        switch (mode) {
            case SpaceviewerMode.D1:
                return prepare1DSpaceviewerData(data?.values || [], range, colorMapType, totalRunsCount, selectedPoint, isResultElement);
            case SpaceviewerMode.D2:
                return prepare2DSpaceviewerData(data?.values || [], range, colorMapType, totalRunsCount, selectedPoint, isResultElement);
            case SpaceviewerMode.D3:
                return prepareSpaceviewerData(slidersValues, sliders, xAxis?.index, yAxis?.index, data?.values || [], range, colorMapType, totalRunsCount, selectedPoint, isResultElement);
        }
    }, [
        data?.values,
        isResultElement,
        mode,
        range,
        colorMapType,
        selectedPoint,
        sliders,
        slidersValues,
        xAxis?.index,
        yAxis?.index,
        totalRunsCount,
    ]);
    const colorMapLabel = useMemo(() => {
        if (appearance !== SpaceviewerAppearance.SimulationResult) {
            return INSIGHT_LABEL;
        }
        return legendData.sourceLabel || DEFAULT_LABEL;
    }, [appearance, legendData]);
    const someResultBiased = useMemo(() => data?.values?.some(({ errorCount }) => errorCount > 0), [data]);
    const onSliderChange = useCallback((index, value) => {
        const newValues = [...slidersValues];
        newValues[index] = value;
        setSlidersValues(newValues);
        setSelectedPoint(undefined);
        setIsInOptimum(false);
    }, [slidersValues]);
    const onXAxisChange = useCallback(({ value }) => {
        const currentXAxis = { ...xAxis };
        if (value === yAxis?.index) {
            setXAxis(yAxis);
            setYAxis(currentXAxis);
            changeSelectedAxisIndexes([yAxis?.index, currentXAxis?.index]);
        }
        else {
            const { newAxis, sliders: newSliders } = changeAxis(sliders, value);
            setXAxis(newAxis);
            setSliders([...newSliders, currentXAxis]);
            const newSliderValues = [...slidersValues];
            newSliderValues[currentXAxis.index] = slidersInitialValues[currentXAxis.index];
            setSlidersValues(newSliderValues);
            changeSelectedAxisIndexes([newAxis?.index, yAxis?.index]);
        }
    }, [changeSelectedAxisIndexes, sliders, slidersInitialValues, slidersValues, xAxis, yAxis]);
    const onYAxisChange = useCallback(({ value }) => {
        const currentYAxis = { ...yAxis };
        if (value === xAxis?.index) {
            setYAxis(xAxis);
            setXAxis(currentYAxis);
            changeSelectedAxisIndexes([currentYAxis?.index, xAxis?.index]);
        }
        else {
            const { newAxis, sliders: newSliders } = changeAxis(sliders, value);
            setYAxis(newAxis);
            setSliders([...newSliders, currentYAxis]);
            const newSliderValues = [...slidersValues];
            newSliderValues[currentYAxis.index] = slidersInitialValues[currentYAxis.index];
            setSlidersValues(newSliderValues);
            changeSelectedAxisIndexes([xAxis?.index, newAxis?.index]);
        }
    }, [changeSelectedAxisIndexes, sliders, slidersInitialValues, slidersValues, xAxis, yAxis]);
    const handleGraphClick = useCallback(({ points }) => {
        const pointIndex = points[0].customdata;
        const point = data?.values?.[pointIndex];
        setSelectedPoint(point ? { index: pointIndex, data: point } : undefined);
        setSlidersValues(point?.values);
        setIsInOptimum(false);
    }, [data?.values]);
    const showOptimum = useCallback(() => {
        if (optimizedPoint === undefined) {
            return;
        }
        setSelectedPoint(optimizedPoint);
        setSlidersValues(optimizedPoint.data?.values);
        setIsInOptimum(true);
    }, [optimizedPoint]);
    const onMenuItemClick = useCallback((colorMapType, colorPoints) => {
        setColorMapType(colorMapType);
        setColorPoints(colorPoints);
    }, [setColorMapType, setColorPoints]);
    useEffect(() => {
        const indexedParameters = getIndexedParameters(data?.parameters, legendData?.parameters);
        let xAxis = null;
        let yAxis = null;
        let sliders = [];
        switch (mode) {
            case SpaceviewerMode.D1:
                xAxis = indexedParameters[0];
                break;
            case SpaceviewerMode.D2:
                xAxis = indexedParameters[0];
                yAxis = indexedParameters[1];
                break;
            case SpaceviewerMode.D3: {
                xAxis =
                    indexedParameters.find((parameter) => parameter.index === selectedAxisIndexes?.[0] ?? 0) ||
                        indexedParameters[0];
                yAxis =
                    indexedParameters.find((parameter) => parameter.index === selectedAxisIndexes?.[1] ?? 1) ||
                        indexedParameters[1];
                sliders = indexedParameters.filter((parameter) => parameter.index !== xAxis?.index && parameter.index !== yAxis?.index);
            }
        }
        const { sliderValues, optimizedPoint: calculatedOptimizedPoint, range, } = prepareInitialSlidersValues([xAxis, yAxis, ...sliders].filter((axis) => !!axis), data.values || []);
        setSlidersInitialValues(sliderValues);
        setRange(appearance === SpaceviewerAppearance.SimulationResult ? range : DEFAULT_RANGE);
        setXAxis(isNotEmpty(xAxis)
            ? {
                ...xAxis,
                colorMap: getColorMap(xAxis, data?.values, appearance === SpaceviewerAppearance.SimulationResult ? range : DEFAULT_RANGE, colorMapType),
            }
            : null);
        setYAxis(yAxis === null
            ? null
            : {
                ...yAxis,
                colorMap: getColorMap(yAxis, data?.values, appearance === SpaceviewerAppearance.SimulationResult ? range : DEFAULT_RANGE, colorMapType),
            });
        setSliders(sliders.map((slider) => ({
            ...slider,
            colorMap: getColorMap(slider, data?.values, appearance === SpaceviewerAppearance.SimulationResult ? range : DEFAULT_RANGE, colorMapType),
        })));
        setOptimizedPoint(calculatedOptimizedPoint);
        setSelectedPoint(calculatedOptimizedPoint);
        setSlidersValues(calculatedOptimizedPoint?.data?.values || []);
        setIsInOptimum(true);
    }, [
        data?.parameters,
        data?.parameters?.length,
        data.values,
        appearance,
        legendData,
        mode,
        selectedAxisIndexes,
        colorPoints,
        colorMapType,
    ]);
    return (React.createElement("div", { className: styles["spaceviewer-root"] },
        React.createElement("div", { className: styles.left },
            React.createElement("div", { ref: widgetRef },
                React.createElement(SpaceviewerGraph, { points: points, xAxis: xAxis, yAxis: yAxis, mode: mode, isEmpty: isEmpty, widgetWidth: widgetWidth, someResultBiased: someResultBiased, handleGraphClick: handleGraphClick }))),
        React.createElement("div", { className: styles.right },
            React.createElement("div", { className: styles["legend-configs"] },
                isNotEmpty(error, true) && (React.createElement("div", { className: styles["error-container"] },
                    React.createElement(TextTruncate, { text: error, className: styles.error }))),
                React.createElement("div", { className: classNames(styles.legend, { [styles["optimization-preview"]]: !isResultElement }) },
                    inProgress && !isResultElement && (React.createElement("div", { className: styles["optimization-preview-in-progress"] },
                        React.createElement(Loader, { size: 24, accented: true }),
                        React.createElement("span", null, "Running..."))),
                    React.createElement("div", { className: styles.colorMap },
                        React.createElement(ColorMap, { range: range, points: colorPoints, colorMapLabel: colorMapLabel, colorMapType: colorMapType, isResultElement: isResultElement, onMenuItemClick: onMenuItemClick })),
                    React.createElement(ProximityMap, { className: styles.proximity })),
                isResultElement && data?.source && (React.createElement("div", { className: styles.legend },
                    React.createElement("div", { className: classNames(styles.colorMap) },
                        React.createElement(Density, { data: data, isEmpty: isEmpty, changeDensity: changeDensity })),
                    React.createElement(ConfidenceMap, { source: data?.source, changeConfidence: changeConfidence, scrollElement: scrollElement, spaceviewerUid: spaceviewerUid || "" }))),
                mode === SpaceviewerMode.D3 && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: styles["axis-configuration"] },
                        React.createElement("div", { className: styles.axis },
                            React.createElement("p", { className: styles["axis-label"] }, "X axis"),
                            React.createElement(Select, { items: paramsForXAxis, onChange: onXAxisChange, itemRenderer: SelectItemRenderer, value: xAxis?.name, classes: {
                                    root: styles.select,
                                    selectButton: styles["select-button"],
                                    portalClassName: styles["select-portal"],
                                }, fixedItemsListWidth: true, disabled: insightError }),
                            xAxis?.source && (React.createElement(TextTruncate, { text: xAxis?.source, className: styles["axis-origination"] }))),
                        React.createElement("div", { className: styles.axis },
                            React.createElement("p", { className: styles["axis-label"] }, "Y axis"),
                            React.createElement(Select, { items: paramsForYAxis, onChange: onYAxisChange, itemRenderer: SelectItemRenderer, value: yAxis?.name, classes: {
                                    root: styles.select,
                                    selectButton: styles["select-button"],
                                    portalClassName: styles["select-portal"],
                                }, fixedItemsListWidth: true, disabled: insightError }),
                            yAxis?.source && (React.createElement(TextTruncate, { text: yAxis?.source, className: styles["axis-origination"] })))),
                    React.createElement("div", { className: styles.sliders }, sliders.map((slider) => (React.createElement("div", { key: `${slider.id}-${slider.name}-${slider.source}`, className: classNames(styles.slider, { [styles["compact"]]: sliders.length > 3 }) },
                        slider.source && (React.createElement(TextTruncate, { title: slider.simulationName, text: slider.source, className: styles.target })),
                        React.createElement(Slider, { axis: slider, value: slidersValues[slider.index], initialValue: slidersInitialValues[slider.index], onChange: (value) => onSliderChange(slider.index, value), disabled: isEmpty })))))))),
            isResultElement ? (React.createElement(ResultElementLegend, { parameters: data?.parameters, slidersValues: slidersValues, legendData: legendData, selectedPoint: selectedPoint, totalRunsCount: totalRunsCount, range: range, colorMapType: colorMapType, source: data.source, elementId: elementId, sourceResultState: sourceResult?.state })) : (React.createElement(OptimizationPreviewLegend, { parameters: data?.parameters, slidersValues: slidersValues, legendData: legendData, selectedPoint: selectedPoint, error: error, insightError: insightError })),
            !isEmpty && appearance !== SpaceviewerAppearance.SimulationResult && (React.createElement("div", { className: styles["button-container"] },
                React.createElement(Button, { label: "Show optimum", style: ButtonStyle.Secondary, onClick: showOptimum, disabled: isInOptimum }))))));
};
export default Spaceviewer;
